/* eslint-disable react/no-danger */

'use client'

import { useSearchParams } from 'next/navigation'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
/* eslint-disable @typescript-eslint/no-floating-promises */
import ButtonWithDisclaimer from '@components/common/AutoWidgetButton/buttonWithDisclaimer'
import IllogicalFlowModal from '@components/common/IllogicalFlowModal/illogicalFlowModal'
import NewFlowBanner from '@components/common/NewFlowBanner/newFlowBanner'
import { getR2d2Payload } from '@utils/r2d2Payload'
import { lobSegmentEventProperties, segmentEventCommonPropFunc, semAutoR2d2properties } from '@utils/segmentProperties'
import React, { useEffect, useState } from 'react'
import { AutoProduct, AutoWidgetVariant } from 'src/enums/AutoProduct'
import { ButtonType } from 'src/enums/ButtonType'
import { REGISTRATION_NUMBER_QUERY_KEY } from '@constants/queryParamsKeys'
import { useCarJourney } from '@context/CarJourneyContext/CarJourneyContext'
import { useUserContext } from '@context/UserContext/UserContenxt'
import { useEnvVariablesClientConfig } from '@context/EnvClientContext/EnvVariablesClientContext'
import { getConfig } from '@configs/envConfig'
import { isTurnstileEnabledForProduct, preloadTurnstile } from '@utils/turnstile/turnstile'
import logger from '@utils/logger'
import styles from './autoWidget.module.scss'
import {
  enterJourneyEkind,
  enterJourneyEventName,
  autoWidgetJourney,
  newFlowJourneyEkind,
  newFlowJourneyEventName,
  newFlowJourney,
} from './config'
import { handleRegNumberValidation, handleSubmit } from './utils'
import { type AutoWidgetProps } from './interfaces'
import ContinueInsuringCard from './carJourneyWidget'

export const proposalAPIResponseInitial = {
  error_context: '',
  error_message: '',
  error_code: '',
  request: { response: '' },
}

const proposalAPIUrl = '/motororchestrator/api/v2/proposals'

const AutoWidgetOldLayout = (props: AutoWidgetProps) => {
  const {
    widgetHeading,
    disclaimer,
    inputPlaceholder,
    widgetCtaText,
    segmentEventCommonProperties,
    newFlowBanner,
    widgetType,
    product,
    newFlowRedirectionUrl,
    widgetVariant,
    isBottomFloater,
    isSemPage,
    r2d2Commonproperties,
    regexPatternsData,
  } = props
  const queryParams = useSearchParams()
  const [regNo, setRegNo] = useState(queryParams.get(REGISTRATION_NUMBER_QUERY_KEY) || '')
  const [isFetching, setIsFetching] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errors, setErrors] = useState({})
  const [showIllogicalFlowModal, setShowIllogicalFlowModal] = useState(false)
  const [proposalAPIResponse, setProposalAPIResponse] = useState(proposalAPIResponseInitial)
  const [properties, setProperties] = useState({})
  const [newFlowR2d2Props, setNewFlowR2d2Props] = useState({})
  const { userData } = useUserContext()
  const envConfig = useEnvVariablesClientConfig()
  const turnstileConfig = getConfig(envConfig.ACKO_ENV)?.turnstile
  const { carJourney } = useCarJourney()
  const [segmentEventProps, setSegmentEventProps] = useState({})
  const journeyData = carJourney?.data || []

  const additionalProperties = {
    cta_text: widgetCtaText,
    funnel: 'entry',
    communication_pincode: '',
    widget_type: 'purple_widget',
    journey: autoWidgetJourney[product][widgetType],
  }

  const fetchAndInvokeHandleSubmit = async () => {
    await handleSubmit({
      proposalAPIUrl,
      regNo,
      userData,
      setIsFetching,
      setErrors,
      setProposalAPIResponse,
      setShowIllogicalFlowModal,
      InsuretrackEventName: enterJourneyEventName[product][widgetType],
      InsureSegmentTrackProps: {
        ...properties,
        ...additionalProperties,
        ...getR2d2Payload({
          isSemPage,
          ekind: enterJourneyEkind[product][widgetType],
          edata: {
            product_state: isSemPage ? 'Marketing Landing Pages' : null,
            screen: 'registration_number_screen',
            registration_number: regNo,
          },
          odata: { product },
        }),
      },
      inputCtaText: widgetCtaText,
      product,
      setShowError,
      regexPatternsData,
      turnstileConfig,
    })
    logger.info('Errors are: ', JSON.stringify(errors))
  }

  useEffect(() => {
    if (!isTurnstileEnabledForProduct(turnstileConfig, product)) return

    void preloadTurnstile(turnstileConfig || {}).catch(error => {
      logger.error('Turnstile preload failed:', error)
    })
  }, [product, turnstileConfig])

  useEffect(() => {
    const getProperties = async () => {
      const additionalLobEventProperties = (await lobSegmentEventProperties[product]?.(isSemPage)) || {}
      const props = await segmentEventCommonPropFunc(segmentEventCommonProperties)
      setProperties({ ...props })
      setSegmentEventProps({ ...props, ...additionalLobEventProperties })
    }
    getProperties()
    setNewFlowR2d2Props(
      getR2d2Payload({
        ekind: newFlowJourneyEkind[product][widgetType],
        edata: { journey: product === AutoProduct.Car ? 'new_car' : 'new_bike', lob: 'direct', pa_cover: false },
        odata: { product },
        r2d2CommonProperties: r2d2Commonproperties,
      })
    )
  }, [])

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setShowError(true)
      isSemPage &&
        trackEvent('error_triggered_on_cta', {
          ...properties,
          ...additionalProperties,
          ...getR2d2Payload({
            isSemPage,
            ekind: 'invalid_reg_number',
            edata: { ...semAutoR2d2properties },
            odata: { product },
          }),
        })
    } else {
      setShowError(false)
    }
  }, [errors])

  useEffect(() => {
    // Using this to prevent scrolling when overlay is open
    showIllogicalFlowModal
      ? (document.documentElement.style.overflowY = 'hidden')
      : (document.documentElement.style.overflowY = 'scroll')
  }, [showIllogicalFlowModal])

  const onBlurSegmentEvent = async () => {
    const eventProperties = {
      ...properties,
      ...additionalProperties,
      registration_number: regNo,
      ...getR2d2Payload({
        ekind: product === AutoProduct.Car ? 'registration_number_entered' : 'tap_btn_enter_your_bike_number',
        edata: { ...semAutoR2d2properties },
        odata: { product },
      }),
    }
    if (isSemPage) {
      await trackEvent('tap_registration_number_cta', eventProperties)
    }
  }

  return (
    <div
      className={`${styles.autoWidgetRootContainer} ${!isBottomFloater && styles.semDesktopVariant} ${
        isBottomFloater ? styles.bottomFloaterRoot : ''
      } ${!isSemPage && styles.seoPurpleVariant}`}>
      {(() => {
        const matchingJourney = (journeyData as any)?.find((item: any) => item?.vehicle_type === product)

        return matchingJourney ? (
          <>
            <ContinueInsuringCard
              commonSegmentEventProps={{ ...segmentEventProps }}
              imageUrl={matchingJourney.logo_url}
              price={matchingJourney.net_premium}
              redirectUrl={matchingJourney.redirect_link}
              text={`${matchingJourney.mmv?.make} ${matchingJourney.mmv?.model}`}
            />
            <div className={styles.carJourneySeperation}>
              <div>OR</div>
            </div>
          </>
        ) : null
      })()}
      <div
        className={`${styles.widgetContainer} ${isBottomFloater ? styles.bottomFloater : ''} ${
          widgetVariant === AutoWidgetVariant.SemVariant ? styles.semVariant : styles.seoVariant
        }`}>
        <div
          className={`${styles.widgetHeading} ${styles.showOnlyMweb} ${
            isBottomFloater ? styles.bottomFloaterHeading : ''
          }`}>
          {widgetHeading}
        </div>
        <div className={styles.inputContainer}>
          <input
            className={styles.input}
            type="text"
            placeholder={inputPlaceholder || `Enter your ${product} number`}
            name={`${product}Number`}
            value={regNo}
            onChange={event => {
              setRegNo(event.target.value.toUpperCase())
              if (Object.entries(errors).length > 0) {
                setErrors({})
              }
            }}
            onBlur={() => {
              onBlurSegmentEvent()
              regNo && handleRegNumberValidation(regNo, setErrors, product, regexPatternsData)
            }}
          />
          {showError ? <div className={styles.errorContainer}>{errors[product]}</div> : null}
          {!isBottomFloater ? (
            <div className={styles.showOnlyWeb}>
              <ButtonWithDisclaimer
                ctaText={widgetCtaText}
                onClick={fetchAndInvokeHandleSubmit}
                type={ButtonType.Green}
                loading={isFetching}
                disabled={isFetching}
              />
            </div>
          ) : null}
        </div>
        {isBottomFloater ? (
          <div className={styles.showOnlyWeb}>
            <ButtonWithDisclaimer
              ctaText={widgetCtaText}
              onClick={fetchAndInvokeHandleSubmit}
              type={ButtonType.Green}
              loading={isFetching}
              disabled={isFetching}
            />
          </div>
        ) : null}
        <div className={styles.showOnlyMweb}>
          <ButtonWithDisclaimer
            disclaimer={disclaimer}
            ctaText={widgetCtaText}
            onClick={fetchAndInvokeHandleSubmit}
            type={ButtonType.Green}
            loading={isFetching}
            disabled={isFetching}
          />
        </div>
        {!isSemPage && <p className={styles.disclaimer}>{disclaimer}</p>}
      </div>

      {showError ? <div className={styles.semWebErrorContainer}>{errors[product]}</div> : null}
      {isSemPage && !isBottomFloater ? <div className={`${styles.widgetDivider} ${styles.showOnlyWeb}`}>OR</div> : null}
      <NewFlowBanner
        title={newFlowBanner?.heading || `Buying a brand new ${product}?`}
        subTitle={newFlowBanner?.subHeading || `Great discount’s on your new ${product} insurance`}
        ctaText={newFlowBanner?.ctaText || 'View plans'}
        redirectionUrl={newFlowRedirectionUrl}
        icon={{
          src: newFlowBanner?.iconSrc,
          alt: newFlowBanner?.iconAlt,
          position: newFlowBanner?.iconPosition,
          width: 72,
          height: 54,
        }}
        InsuretrackEventName={newFlowJourneyEventName[product][widgetType]}
        InsureSegmentTrackProps={{
          ...properties,
          ...additionalProperties,
          ...newFlowR2d2Props,
          journey: newFlowJourney[product][widgetType],
        }}
        widgetVariant={widgetVariant}
        isBottomFloater={isBottomFloater}
        isOldLayout
        isSeoPurpleVariant={!isSemPage}
      />
      {showIllogicalFlowModal ? (
        <IllogicalFlowModal
          regNo={regNo}
          errorCode={proposalAPIResponse?.error_code}
          illogicalFlowData={proposalAPIResponse?.error_context}
          setProposalAPIResponse={setProposalAPIResponse}
          product={product}
          showIllogicalFlowModal={showIllogicalFlowModal}
          setShowIllogicalFlowModal={setShowIllogicalFlowModal}
          secondaryCta={() => {
            setShowIllogicalFlowModal(false)
            setProposalAPIResponse(proposalAPIResponseInitial)
          }}
          onClose={() => {
            setShowIllogicalFlowModal(false)
            setProposalAPIResponse(proposalAPIResponseInitial)
          }}
          identifiedVehicleType={proposalAPIResponse?.error_context}
        />
      ) : null}
    </div>
  )
}

export default AutoWidgetOldLayout
